// Mini modal with picture having a gray background around it (two "blocks")
import React, { useContext, useEffect, useRef, useState } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { css, useTheme } from '@emotion/react';
import _ from 'lodash';
import {
  EditOfferPageContext,
  PreviewDevice,
} from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import { MobileDevicePreview } from '@bbkAdminComponents/preview-device/mobile-device';
import {
  type RestModalEmailStructuredModal,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import FunnelCheckbox from '@bbkFunnel/components/checkbox/funnel-checkbox';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import {
  FunnelButton,
  FunnelButtonIcon,
  FunnelTextArea,
  getSvgIcon,
} from '@bbkFunnel/components/v2';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import {
  ModalDesktopInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

type Props = {
  modalInfo: RestModalEmailStructuredModal;
  close: () => void;
  handleAction: (
    forwards: RestModalForwards,
    { message }: { message: string }
  ) => void;
};

export const StructuredContent: React.FC<Props> = (props) => {
  const {
    modalInfo: {
      id,
      dismissable,
      requires: {
        forwards,
        info: { title, placeholder, optionsExplanation, options, allowOther },
      },
      mobileLayout,
    },
    close,
    handleAction,
  } = props;
  const { mobileView, tabletView } = useMediaQueries();
  const [messageContent, setMessageContent] = useState('');
  const [otherActive, setOtherActive] = useState(false);
  const theme = useTheme() as EmotionTheme;

  const offerContext = useContext(EditOfferPageContext);
  const isPreview = !!offerContext;
  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-header`, headerRef),
    [headerRef]
  );
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const optsRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-opts`, optsRef), [optsRef]);
  const btnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-btn`, btnRef), [btnRef]);

  function handleForwards() {
    console.log(messageContent);
    // verified by a active or disabled button
    handleAction(
      { ...forwards, sync: { ...forwards.sync, message: messageContent } },
      { message: messageContent }
    );
  }

  const Icon = forwards.icon ? (
    <FunnelButtonIcon>{getSvgIcon(forwards.icon)}</FunnelButtonIcon>
  ) : null;

  const color = theme.modalTextColor || theme.textColor;
  const equalButtonSize = theme.complianceEqualButtonSize;
  const isMobilePreview =
    isPreview && offerContext?.previewDevice === PreviewDevice.MOBILE;

  const actionButton = (
    <FunnelButton
      onClick={handleForwards}
      specificStyles={{ width: '100%' }}
      classes="d-flex align-items-center justify-content-center"
      disabled={messageContent === ''}
      ref={btnRef}
    >
      {Icon}
      <span style={{ paddingLeft: '5px' }}>{forwards.text}</span>
    </FunnelButton>
  );

  const content = (
    <ModalDesktopInnerSpacer
      css={css`
        ${mobileView && 'padding: 12px 1rem; flex: 1 1 auto;'}
        ${tabletView && !isPreview && 'width: 450px;'}
      `}
      className={cs('email-structured', 'display-grid', 'inner-spacer', {
        mobile: mobileView,
      })}
    >
      <OfferTitleMobile
        ref={headerRef}
        title={<TitleTheme title={title} ref={titleRef} />}
        close={close}
        dismissable={dismissable}
      />
      <div css={{ marginBottom: '1rem' }}>
        <div css={{ marginBottom: '1rem' }} ref={descRef}>
          <CustomerHtml value={optionsExplanation} />
        </div>
        <div ref={optsRef}>
          {options.map((opt, i) => (
            <FunnelCheckbox
              key={i}
              label={opt}
              name={_.kebabCase(opt)}
              wrapperStyles={{ marginBottom: '.3rem' }}
              checked={messageContent === opt && messageContent !== ''}
              handleChange={() => {
                setOtherActive(false);
                setMessageContent(opt === messageContent ? '' : opt);
              }}
              modal
            />
          ))}
          {allowOther && (
            <>
              <FunnelCheckbox
                label={theme.dictionaryOther ?? 'Other'}
                name="otherStructured"
                wrapperStyles={{ marginBottom: '1rem' }}
                checked={otherActive}
                handleChange={() => {
                  setOtherActive(!otherActive);
                  setMessageContent('');
                }}
                modal
              />
              {otherActive && (
                <FunnelTextArea
                  name="feedback"
                  value={messageContent}
                  rows={2}
                  placeholder={placeholder}
                  onChange={(e) => setMessageContent(e.target.value)}
                  onBlur={(e) => setMessageContent(e.target.value)}
                  additionalStyles={{
                    color,
                    border: `2px solid ${new TinyColor(color)
                      .setAlpha(0.4)
                      .toHexString()}`,
                  }}
                  maxLength={240}
                  modalTextColor
                />
              )}
            </>
          )}
        </div>
      </div>
      {equalButtonSize && (mobileView || isMobilePreview) ? (
        <div className="tw-px-[11px]">{actionButton}</div>
      ) : (
        actionButton
      )}
    </ModalDesktopInnerSpacer>
  );

  if (offerContext && offerContext?.previewDevice === PreviewDevice.MOBILE) {
    return (
      <MobileDevicePreview mobileLayout={mobileLayout}>
        {content}
      </MobileDevicePreview>
    );
  }

  return content;
};
